async function transformBytes(bytes, StreamType) {
  if (typeof StreamType !== 'function') {
    throw new Error('当前浏览器不支持语音流压缩');
  }
  const stream = new StreamType('gzip');
  const output = new Response(stream.readable).arrayBuffer();
  const writer = stream.writable.getWriter();
  await writer.write(bytes);
  await writer.close();
  return new Uint8Array(await output);
}

export const ASR_FIRST_AUDIO_SEQUENCE = 2;

export const shouldReportASRClose = (active, completed) => active && !completed;

export const gzip = (bytes) => transformBytes(bytes, window.CompressionStream);
export const gunzip = (bytes) =>
  transformBytes(bytes, window.DecompressionStream);

export function floatToPCM16(samples) {
  const pcm = new Int16Array(samples.length);
  for (let index = 0; index < samples.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, samples[index]));
    pcm[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return pcm;
}

export function resamplePCM(samples, sourceRate, targetRate = 16000) {
  if (sourceRate === targetRate) return floatToPCM16(samples);
  const length = Math.max(1, Math.round((samples.length * targetRate) / sourceRate));
  const output = new Float32Array(length);
  const ratio = sourceRate / targetRate;
  for (let index = 0; index < length; index += 1) {
    const sourceIndex = index * ratio;
    const left = Math.floor(sourceIndex);
    const right = Math.min(left + 1, samples.length - 1);
    const weight = sourceIndex - left;
    output[index] = samples[left] * (1 - weight) + samples[right] * weight;
  }
  return floatToPCM16(output);
}

function pcmBytes(samples) {
  const pcm = samples instanceof Int16Array ? samples : floatToPCM16(samples);
  const bytes = new Uint8Array(pcm.length * 2);
  const view = new DataView(bytes.buffer);
  pcm.forEach((sample, index) => view.setInt16(index * 2, sample, true));
  return bytes;
}

function frame(messageType, flags, serialization, payload, sequence) {
  const output = new Uint8Array(12 + payload.length);
  output.set([0x11, (messageType << 4) | flags, (serialization << 4) | 0x01, 0]);
  const view = new DataView(output.buffer);
  view.setInt32(4, sequence);
  view.setUint32(8, payload.length);
  output.set(payload, 12);
  return output.buffer;
}

export async function encodeFullRequest(request, sequence = 1, compressor = gzip) {
  const payload = new TextEncoder().encode(JSON.stringify(request));
  return frame(0x1, 0x1, 0x1, await compressor(payload), sequence);
}

export async function encodeAudioFrame(samples, sequence, final = false, compressor = gzip) {
  if (sequence <= 0) throw new Error('ASR 音频序号必须大于 0');
  const payload = await compressor(pcmBytes(samples));
  return frame(0x2, final ? 0x3 : 0x1, 0x0, payload, final ? -sequence : sequence);
}

function recognizedText(message) {
  if (typeof message?.result?.text === 'string') return message.result.text;
  if (typeof message?.text === 'string') return message.text;
  const first = Array.isArray(message?.result) ? message.result[0] : null;
  return typeof first?.text === 'string' ? first.text : '';
}

export async function decodeServerFrame(data, decompressor = gunzip) {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  if (bytes.length < 12 || bytes[0] >> 4 !== 1) {
    throw new Error('无效的 ASR 响应帧');
  }
  const headerSize = (bytes[0] & 0x0f) * 4;
  const messageType = bytes[1] >> 4;
  const flags = bytes[1] & 0x0f;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let offset = headerSize;
  let sequence = 0;
  let errorCode = 0;
  if (messageType === 0x0f) {
    errorCode = view.getUint32(offset);
    offset += 4;
  } else if (flags === 0x1 || flags === 0x3) {
    sequence = view.getInt32(offset);
    offset += 4;
  }
  const payloadSize = view.getUint32(offset);
  offset += 4;
  if (offset + payloadSize !== bytes.length) throw new Error('ASR 响应长度无效');
  let payload = bytes.slice(offset);
  if ((bytes[2] & 0x0f) === 1) payload = await decompressor(payload);
  const message = payload.length
    ? JSON.parse(new TextDecoder().decode(payload))
    : {};
  const error = messageType === 0x0f
    ? { code: errorCode, message: message.message || '语音识别失败' }
    : null;
  return {
    text: recognizedText(message),
    final: flags === 0x3 || sequence < 0,
    error,
  };
}

export function buildASRWebSocketURL(channelId, location = window.location) {
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${location.host}/api/channel/${channelId}/voice-test/asr`;
}
